import Button from '@mui/material/Button'
import {
  Box,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'
import { AnalyzedPage } from '../models/File'
import { replacePage, selectPage, switchPageCheckbox } from '../actions/files'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../store'
import TruncatedTextWithTooltip from './TruncatedTextWithTooltip'
import { Close } from '@mui/icons-material'

interface ReplaceblePagesProps {
  title: string
  pages: AnalyzedPage[]
  newPageId: string
  addPageHandle: (pageId: string) => void
  removePageHandle: (pageId: string) => void
  goBackHandle: () => void
}

const ReplaceblePages: React.FC<ReplaceblePagesProps> = ({
  title,
  pages,
  newPageId,
  addPageHandle,
  removePageHandle,
  goBackHandle,
}) => {
  const drawings = useSelector((state: RootState) => state.drawings)
  const selectedPageId = drawings.selectedPageId
  const dispatch = useDispatch<any>()

  const handleImageCheckboxClick = (pageId: string) => {
    dispatch(switchPageCheckbox({ pageId }))
  }

  const handleReplaceButtonClick = (pageId: string) => {
    dispatch(replacePage({ pageId, newPageId: selectedPageId }))
    dispatch(selectPage({ pageId: '' }))
  }

  return (
    <Box>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography
            color='#989898'
            variant='body1'
            sx={{
              fontWeight: 500,
              fontSize: '16px',
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            pb: 1,
            '&::-webkit-scrollbar': {
              width: '20px',
              height: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#56003D',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#56003D',
            },
            justifyContent: pages.length === 0 ? 'center' : 'flex-start',
            minHeight: '144px',
          }}
        >
          {pages.map((page, index) => (
            <Grid key={index}>
              <Box
                sx={{
                  width: 179,
                  display: 'inline-block',
                  flexShrink: 0,
                  mr: '18px',
                }}
              >
                <Box sx={{ position: 'relative', width: '100%', height: 118 }}>
                  <Checkbox
                    checked={page.isChecked}
                    onChange={() => handleImageCheckboxClick(page.id)}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      padding: '2px',
                      color: '#3F69FF',
                      '&.Mui-checked': {
                        color: '#3F69FF',
                      },
                    }}
                  />
                  {page.isLoading ? (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        boxSizing: 'border-box',
                        flex: '0 0 auto',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        borderRadius: '2px',
                        border: '1px solid #B6B6B6',
                        boxShadow:
                          '0px 3.534px 5.891px 0px rgba(0, 0, 0, 0.25)',
                      }}
                    >
                      <CircularProgress sx={{ color: '#56003D' }} />
                    </Box>
                  ) : (
                    <img
                      src={page.image}
                      alt='floor'
                      style={{
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        borderRadius: '2px',
                        border: '1px solid #B6B6B6',
                        boxShadow:
                          '0px 3.534px 5.891px 0px rgba(0, 0, 0, 0.25)',
                      }}
                    />
                  )}
                  <IconButton
                    onClick={() => removePageHandle(page.id)}
                    sx={{
                      ml: 'auto',
                      backgroundColor: '#F5F5F5',
                      width: '12px',
                      height: '12px',
                      boxShadow: '0px 3.534px 5.891px 0px rgba(0, 0, 0, 0.25)',
                      position: 'absolute',
                      top: '3px',
                      right: '3px',
                    }}
                  >
                    <Close
                      sx={{
                        width: '12px',
                        height: '12px',
                      }}
                    />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 1,
                    width: '100%',
                  }}
                >
                  <TruncatedTextWithTooltip
                    text={page.name!}
                    color='#04002E'
                    fontSize={12}
                  ></TruncatedTextWithTooltip>
                  <Button
                    variant='contained'
                    disabled={!!!newPageId}
                    size='small'
                    onClick={() => handleReplaceButtonClick(page.id)}
                    sx={{
                      fontSize: '8px',
                      borderRadius: '2px',
                      textTransform: 'capitalize',
                      backgroundColor: '#3F69FF',
                      pt: '2px',
                      pb: '2px',
                    }}
                  >
                    Replace
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
          {pages.length < 4 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: newPageId ? 'flex-start' : 'center',
                justifyContent: newPageId ? 'flex-start' : 'center',
                width: newPageId ? '30px' : '179px',
                ml: '18px',
              }}
            >
              {newPageId ? (
                <IconButton
                  disableRipple
                  onClick={() => addPageHandle(selectedPageId)}
                  sx={{ p: 0 }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='22'
                    height='118'
                    viewBox='0 0 22 118'
                    fill='none'
                  >
                    <path
                      d='M11.0004 54.2168V58.9994M11.0004 58.9994V63.782M11.0004 58.9994H6.21777M11.0004 58.9994H15.783'
                      stroke='#1705E5'
                      strokeLinecap='square'
                      strokeLinejoin='round'
                    />
                    <circle cx='11' cy='59' r='10.5' stroke='#1705E5' />
                    <line
                      x1='11.5'
                      y1='72'
                      x2='11.5'
                      y2='118'
                      stroke='#C6DEFF'
                      strokeDasharray='2 2'
                    />
                    <line
                      x1='11.5'
                      y1='2.18557e-08'
                      x2='11.5'
                      y2='46'
                      stroke='#C6DEFF'
                      strokeDasharray='2 2'
                    />
                  </svg>
                </IconButton>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '9px',
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='15'
                    viewBox='0 0 16 15'
                    fill='none'
                  >
                    <path
                      d='M8 5.69635V8.58458M8 11.4728H8.00682M7.0556 2.00789L1.44595 12.2659C1.13481 12.8348 0.979233 13.1193 1.00223 13.3528C1.02228 13.5565 1.12307 13.7415 1.2795 13.8619C1.45884 14 1.76935 14 2.39035 14H13.6096C14.2307 14 14.5412 14 14.7205 13.8619C14.8769 13.7415 14.9777 13.5565 14.9978 13.3528C15.0208 13.1193 14.8652 12.8348 14.554 12.2659L8.9444 2.00789C8.63437 1.44096 8.47935 1.15749 8.27711 1.06228C8.1007 0.979239 7.8993 0.979239 7.72289 1.06228C7.52065 1.15749 7.36563 1.44096 7.0556 2.00789Z'
                      stroke='#FF2E79'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  <Box
                    sx={{
                      color: '#1705E5',
                      borderRadius: '100px',
                      border: '1px solid #1705E5',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: 'normal',
                      padding: '4px 13px',
                    }}
                  >
                    We might be missing files
                  </Box>
                  <Typography
                    sx={{
                      color: '#04002E',
                      fontSize: '12px',
                      lineHeight: 'normal',
                    }}
                  >
                    Please choose files from below
                  </Typography>
                  {!pages.length && (
                    <Button
                      variant='text'
                      disableRipple
                      onClick={goBackHandle}
                      sx={{
                        textTransform: 'none',
                        color: '#04002E',
                        p: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '9px',
                        }}
                      >
                        or{' '}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '9px',
                          fontWeight: 500,
                          ml: '4px',
                          textDecoration: 'underline',
                        }}
                      >
                        go back to upload a new file
                      </Typography>
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                maxWidth: '51px',
                fontSize: '8px',
                lineHeight: 'normal',
                color: '#1705E5',
                textWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                pb: '26px',
              }}
            >
              Reached Max 4 Floors
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default ReplaceblePages
